import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Озвучка слів голосом пристрою (Web Speech API).
 *
 * Голос обирається під мову уроку (`lang`, BCP-47 з бекенда): серед голосів пристрою
 * беруться лише ті, що збігаються за мовою, і найякісніший ставиться першим.
 * Без правильної мови слово «apple» прочитав би український голос — саме тому
 * `lang` обов'язковий і приходить з напряму програми.
 */

const RATE_KEY = 'vocabularyRate';
const SLOW_RATE_KEY = 'vocabularySlowRate';
const VOICE_KEY = 'vocabularyVoiceV3';

export const RATE_DEFAULT = 1;
export const SLOW_RATE_DEFAULT = 0.6;

/**
 * Швидкість зберігається окремо для кожної мови курсу: { 'fr-FR': 0.9, 'en-US': 1.2 }.
 * Старий формат (одне число для всіх) читається як значення за замовчуванням для будь-якої мови.
 */
const readRateMap = (key) => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
    const legacy = Number(raw);

    return legacy > 0 ? { '*': legacy } : {};
  } catch (e) {
    const legacy = Number(localStorage.getItem(key));

    return legacy > 0 ? { '*': legacy } : {};
  }
};

const readRate = (key, lang, fallback) => {
  const map = readRateMap(key);
  const value = Number(map[lang] ?? map['*']);

  return value > 0 ? value : fallback;
};

const readVoiceMap = () => {
  try {
    return JSON.parse(localStorage.getItem(VOICE_KEY)) || {};
  } catch (e) {
    return {};
  }
};

const save = (key, value) => {
  try {
    localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  } catch (e) {
    // приватний режим — налаштування просто не запам'ятається
  }
};

/**
 * Голоси, які обрало керівництво школи (є на iPhone, iPad і Mac — у Safari та Chrome).
 * На пристроях без них (Windows, Android) береться найкращий із наявних за voiceScore.
 * `label` — як голос підписаний у налаштуваннях.
 */
const PREFERRED_VOICES = {
  fr: { name: 'amelie', label: 'Амелі' },
  en: { name: 'karen', label: 'Карен' },
  zh: { name: 'meijia', label: 'Мейдзя' },
  ko: { name: 'yuna', label: 'Юна' },
  de: { name: 'anna', label: 'Анна' },
  es: { name: 'paulina', label: 'Пауліна' },
  ja: { name: 'kyoko', label: 'Кіоко' },
  pl: { name: 'zosia', label: 'Зосія' },
  it: { name: 'alice', label: 'Еліс' },
  tr: { name: 'yelda', label: 'Айла' },
};

/**
 * Жартівливі голоси Apple (є для багатьох мов у macOS/iOS). У список потрапляють лише тоді,
 * коли для мови немає жодного іншого робочого голосу: краще такий голос, ніж тиша.
 */
const NOVELTY_VOICES = /^(eddy|flo|grandma|grandpa|reed|rocko|sandy|shelley|albert|bad news|bahh|bells|boing|bubbles|cellos|good news|jester|organ|superstar|trinoids|whisper|wobble|zarvox|junior|ralph|fred|kathy)\b/;

/** Серед жартівливих ці звучать найближче до звичайних — у такому порядку й беремо. */
const NOVELTY_ORDER = ['flo', 'shelley', 'sandy', 'reed', 'eddy'];

const isNovelty = (voice) => NOVELTY_VOICES.test(plainName(voice)) || NOVELTY_VOICES.test(uriName(voice));

const noveltyRank = (voice) => {
  const index = NOVELTY_ORDER.findIndex((name) => plainName(voice).startsWith(name) || uriName(voice).startsWith(name));

  return index < 0 ? NOVELTY_ORDER.length : index;
};

/**
 * «Німі» голоси: є у списку, але не говорять (у macOS голос значиться, а не завантажений —
 * так Arc показує «Юна»: фраза «закінчується» за 70 мс без звуку). Помічаємо їх, щойно
 * спіймали, і більше не обираємо до перезавантаження сторінки. Спільно для всіх кнопок.
 */
const silentVoices = new Set();

/** «Amélie (Enhanced)» → «amelie (enhanced)»: без діакритики, щоб порівнювати імена. */
const plainName = (voice) => String(voice.name || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/**
 * Arc і Chrome з українським інтерфейсом macOS віддають перекладені назви («Юна» замість «Yuna»),
 * а технічний ідентифікатор лишається англійським: com.apple.voice.compact.ko-KR.Yuna.
 */
const uriName = (voice) => String(voice.voiceURI || '').split('.').pop().toLowerCase();

const preferredFor = (voice) => {
  const preferred = PREFERRED_VOICES[String(voice.lang || '').slice(0, 2).toLowerCase()];

  return preferred && (plainName(voice).startsWith(preferred.name) || uriName(voice).startsWith(preferred.name))
    ? preferred
    : null;
};

/** Підпис голосу в налаштуваннях: обраний школою — українською, решта — як називає пристрій. */
export const voiceLabel = (voice) => {
  const preferred = preferredFor(voice);
  if (!preferred) return voice.name;
  const quality = /premium|преміум/i.test(voice.name) ? ' (преміум)' : /enhanced|покращ/i.test(voice.name) ? ' (покращений)' : '';

  return `${preferred.label}${quality}`;
};

/** Чим вищий бал, тим надійніший і природніший голос: хмарний голос браузера, далі обраний школою, далі нейронні. */
const voiceScore = (voice) => {
  const name = voice.name || '';
  let score = 0;
  if (preferredFor(voice)) score += 10;
  if (/natural|neural/i.test(name)) score += 4;
  if (/google/i.test(name)) score += 3;
  if (/online|premium|enhanced/i.test(name)) score += 2;
  if (!voice.localService) score += 20;

  return score;
};

const sameLanguage = (voice, lang) => {
  const base = String(lang).slice(0, 2).toLowerCase();

  return String(voice.lang || '').replace('_', '-').toLowerCase().startsWith(base);
};

/**
 * Якісний голос: обраний школою, нейронний Microsoft («Natural»/«Online»), Google,
 * Apple «Premium»/«Enhanced» або хмарний. Решта — стандартні голоси системи.
 */
const isQualityVoice = (voice) => Boolean(preferredFor(voice))
  || /natural|neural|online|premium|enhanced|google/i.test(voice.name || '')
  || !voice.localService;

/** Скільки голосів максимум показувати в налаштуваннях — щоб не відволікатись на вибір. */
const MAX_VOICES = 3;

/**
 * Короткий список голосів для мови: без жартівливих; стандартні — лише коли якісних немає;
 * з кількох версій одного голосу («Amélie» і «Amélie (Enhanced)») лишається найкраща.
 */
const shortlistVoices = (voices, lang) => {
  const usable = voices.filter((voice) => sameLanguage(voice, lang) && !silentVoices.has(voice.name));
  const candidates = usable
    .filter((voice) => !isNovelty(voice))
    .sort((a, b) => voiceScore(b) - voiceScore(a));
  const quality = candidates.filter(isQualityVoice);
  const novelty = usable.filter(isNovelty).sort((a, b) => noveltyRank(a) - noveltyRank(b));
  let pool = novelty;
  if (quality.length) pool = quality;
  else if (candidates.length) pool = candidates;
  const seen = new Set();

  return pool.filter((voice) => {
    const base = plainName(voice).split(' (')[0];
    if (seen.has(base)) return false;
    seen.add(base);

    return true;
  }).slice(0, MAX_VOICES);
};

/** Обраний голос: збережений для цієї мови, якщо він досі є на пристрої, інакше найкращий. */
const pickVoice = (list, key) => {
  if (!list.length) return '';
  const saved = readVoiceMap()[key];
  const found = saved && list.find((voice) => voice.name === saved);

  return found ? found.name : list[0].name;
};

/** Скільки чекати onstart, перш ніж вирішити, що движок «проковтнув» фразу, і повторити. */
const START_TIMEOUT_MS = 2500;
/** Пауза після cancel(): Chrome губить utterance, якщо speak() іде одразу за cancel(). */
const AFTER_CANCEL_MS = 80;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Фраза, що «договорилась» швидше за цей час, насправді не прозвучала: навіть один склад
 * на подвійній швидкості триває довше. Так поводиться голос, який не завантажений у системі.
 */
const SILENT_END_MS = 150;

/** Помилки, які означають «ми самі зупинили», а не «звук не вийшов». */
const STOPPED_ERRORS = ['interrupted', 'canceled'];

/**
 * Хмарні голоси Google (Chrome) не читають повільніше за ~0.5×: rate 0.3 і 0.5 звучать однаково.
 * Тому для них фразу з кількох слів сповільнюємо самі — читаємо слово за словом і додаємо між
 * словами паузи так, щоб загальний час відповідав заданій швидкості. Одне слово розтягнути неможливо.
 */
const ENGINE_MIN_RATE = 0.5;
const isRateLimited = (voice) => Boolean(voice) && (/google/i.test(voice.name || '') || !voice.localService);

const useSpeech = (lang) => {
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;
  const isSupported = Boolean(synth && typeof window.SpeechSynthesisUtterance === 'function');

  const [voices, setVoices] = useState([]);
  // true після першого непорожнього списку або після таймера: до того «голосу нема» не показуємо
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const [voiceName, setVoiceName] = useState('');
  // Лічильник «німих» голосів: коли він росте, список голосів і обраний голос перераховуються
  const [silentCount, setSilentCount] = useState(silentVoices.size);
  const [rate, setRateState] = useState(() => readRate(RATE_KEY, lang, RATE_DEFAULT));
  const [slowRate, setSlowRateState] = useState(() => readRate(SLOW_RATE_KEY, lang, SLOW_RATE_DEFAULT));

  // Мова приходить із бекенда після завантаження — перечитати швидкості саме для неї
  useEffect(() => {
    setRateState(readRate(RATE_KEY, lang, RATE_DEFAULT));
    setSlowRateState(readRate(SLOW_RATE_KEY, lang, SLOW_RATE_DEFAULT));
  }, [lang]);
  const [speakingId, setSpeakingId] = useState(null);

  const queueRef = useRef(0);
  // Chrome може прибрати utterance збирачем сміття до onend — тримаємо посилання
  const utteranceRef = useRef(null);

  useEffect(() => {
    if (!isSupported) return undefined;

    const load = () => {
      const list = synth.getVoices();
      setVoices(list);
      if (list.length) setVoicesLoaded(true);
    };
    load();
    synth.addEventListener('voiceschanged', load);
    // Safari/iOS/Firefox віддають список із затримкою і не завжди шлють подію —
    // опитуємо кілька секунд, поки список не з'явиться
    let attempts = 0;
    const poll = setInterval(() => {
      attempts += 1;
      const list = synth.getVoices();
      if (list.length || attempts >= 12) {
        clearInterval(poll);
        load();
        setVoicesLoaded(true);
      }
    }, 250);

    return () => {
      synth.removeEventListener('voiceschanged', load);
      clearInterval(poll);
      synth.cancel();
    };
  }, [isSupported, synth]);

  const languageVoices = useMemo(
    () => shortlistVoices(voices, lang || ''),
    // silentCount не читається всередині, але його зміна означає «список треба перерахувати»
    [voices, lang, silentCount]
  );

  useEffect(() => {
    setVoiceName(pickVoice(languageVoices, lang));
  }, [languageVoices, lang]);

  const selectVoice = useCallback((name) => {
    setVoiceName(name);
    save(VOICE_KEY, { ...readVoiceMap(), [lang]: name });
  }, [lang]);

  const setRate = useCallback((value) => {
    setRateState(value);
    save(RATE_KEY, { ...readRateMap(RATE_KEY), [lang]: value });
  }, [lang]);

  const setSlowRate = useCallback((value) => {
    setSlowRateState(value);
    save(SLOW_RATE_KEY, { ...readRateMap(SLOW_RATE_KEY), [lang]: value });
  }, [lang]);

  // Момент останнього cancel(): наступний speak() робимо не раніше ніж через AFTER_CANCEL_MS
  const cancelledAtRef = useRef(0);

  const stop = useCallback(() => {
    queueRef.current += 1;
    // Safari іноді губить слово, якщо cancel() викликати без активної озвучки
    if (isSupported && (synth.speaking || synth.pending)) {
      synth.cancel();
      cancelledAtRef.current = Date.now();
    }
    setSpeakingId(null);
  }, [isSupported, synth]);

  /**
   * Одна спроба промовити текст. resolve:
   * 'ok' — договорив або його зупинили; 'timeout' — движок так і не почав говорити
   * за START_TIMEOUT_MS; 'failed' — браузер відмовився говорити (нема голосу, заборона тощо);
   * 'silent' — обраний голос «договорив» миттєво, тобто нічого не сказав.
   * `options.voice`: конкретний голос, null — без голосу (браузер обере сам), undefined — обраний.
   */
  const attempt = useCallback((text, options) => new Promise((resolve) => {
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.lang = lang || 'en-US';
    utterance.rate = options.rate || rate;
    const voice = options.voice !== undefined
      ? options.voice
      : voices.find((item) => item.name === voiceName);
    if (voice) utterance.voice = voice;

    let started = false;
    // Відлік від speak(), а не від onstart: якщо onstart запізнився, швидкий кінець не буде хибним
    let spokenAt = 0;
    let finished = false;
    const finish = (value) => {
      if (finished) return;
      finished = true;
      clearTimeout(watchdog);
      resolve(value);
    };
    // Firefox/Chrome інколи «ковтають» першу фразу після завантаження сторінки:
    // подій немає взагалі, промис завис би назавжди. Сторож дає повторити спробу.
    const watchdog = setTimeout(() => {
      if (!started) {
        synth.cancel();
        cancelledAtRef.current = Date.now();
        finish('timeout');
      }
    }, START_TIMEOUT_MS);

    utterance.onstart = () => { started = true; };
    utterance.onend = () => {
      const isSilent = Boolean(voice) && Date.now() - spokenAt < SILENT_END_MS;
      finish(isSilent ? 'silent' : 'ok');
    };
    utterance.onerror = (event) => finish(STOPPED_ERRORS.includes(event?.error) ? 'ok' : 'failed');
    utteranceRef.current = utterance;
    // Chrome після паузи/сну вкладки лишає synth у стані paused — тоді speak() мовчить
    if (synth.paused) synth.resume();
    spokenAt = Date.now();
    synth.speak(utterance);
  }), [synth, lang, rate, voices, voiceName]);

  /**
   * Одна фраза з повтором, якщо звук не пішов.
   * Повертає { ok, duration }: ok = false — браузер так і не зміг її промовити.
   */
  const sayOnce = useCallback(async (text, options) => {
    const sinceCancel = Date.now() - cancelledAtRef.current;
    if (sinceCancel < AFTER_CANCEL_MS) await wait(AFTER_CANCEL_MS - sinceCancel);
    const ticket = queueRef.current;
    let startedAt = Date.now();
    let voice = options.voice !== undefined ? options.voice : voices.find((item) => item.name === voiceName);
    let result = await attempt(text, { ...options, voice: voice || null });
    // Голос німий: запам'ятати і одразу повторити наступним робочим, в кінці — на вибір браузера
    for (let i = 0; i < 3 && result === 'silent' && ticket === queueRef.current; i += 1) {
      if (voice) silentVoices.add(voice.name);
      voice = shortlistVoices(voices, lang || '')[0] || null;
      startedAt = Date.now();
      // eslint-disable-next-line no-await-in-loop
      result = await attempt(text, { ...options, voice });
    }
    if (silentVoices.size !== silentCount) setSilentCount(silentVoices.size);
    if (result !== 'ok' && result !== 'silent' && ticket === queueRef.current) {
      await wait(AFTER_CANCEL_MS);
      startedAt = Date.now();
      result = await attempt(text, { ...options, voice });
    }

    // Якщо поки ми чекали, озвучку зупинили — це не збій
    return { ok: result === 'ok' || ticket !== queueRef.current, duration: Date.now() - startedAt };
  }, [attempt, voices, voiceName, lang, silentCount]);

  /** Промовити один текст; resolve(true) — договорив або зупинили, resolve(false) — звук не вийшов. */
  const say = useCallback(async (text, options = {}) => {
    if (!isSupported || !text) return false;
    const wantedRate = options.rate || rate;
    const voice = voices.find((item) => item.name === voiceName);
    const words = String(text).trim().split(/\s+/);

    if (wantedRate >= ENGINE_MIN_RATE || !isRateLimited(voice) || words.length < 2) {
      const { ok } = await sayOnce(text, options);

      return ok;
    }

    // Голос не вміє повільніше за ENGINE_MIN_RATE: читаємо по слову з паузами.
    // Слово на 0.5× триває T; щоб уся фраза йшла як на wantedRate, після нього пауза T·(0.5/wantedRate − 1).
    const pauseFactor = ENGINE_MIN_RATE / wantedRate - 1;
    const ticket = queueRef.current;
    for (let i = 0; i < words.length; i += 1) {
      if (ticket !== queueRef.current) return true;
      // eslint-disable-next-line no-await-in-loop
      const { ok, duration } = await sayOnce(words[i], { ...options, rate: ENGINE_MIN_RATE });
      if (!ok) return false;
      if (i < words.length - 1 && ticket === queueRef.current) {
        // eslint-disable-next-line no-await-in-loop
        await wait(Math.min(duration * pauseFactor, 4000));
      }
    }

    return true;
  }, [isSupported, sayOnce, rate, voices, voiceName]);

  /**
   * Озвучити довільний текст (фраза з тесту, підказка). `id` — що підсвічувати через speakingId.
   * resolve(false) — браузер не зміг промовити текст (кнопка покаже підказку).
   */
  const speakText = useCallback(async (text, { id = text, slow = false } = {}) => {
    stop();
    const ticket = queueRef.current;
    setSpeakingId(id);
    const ok = await say(text, { rate: slow ? slowRate : rate });
    if (ticket === queueRef.current) setSpeakingId(null);

    return ok;
  }, [stop, say, rate, slowRate]);

  /** Озвучити одне слово; `slow` — швидкістю «повільно» (кнопка 🐢 біля слова). */
  const speakWord = useCallback(async (item, { slow = false } = {}) => {
    stop();
    const ticket = queueRef.current;
    setSpeakingId(item.id);
    await say(item.word, { rate: slow ? slowRate : rate });
    if (ticket === queueRef.current) setSpeakingId(null);
  }, [stop, say, rate, slowRate]);

  return {
    isSupported,
    voicesLoaded,
    hasVoice: languageVoices.length > 0,
    // Список голосів пристрою вже прийшов (на частині Android він завжди порожній, хоч озвучка працює)
    hasVoiceList: voices.length > 0,
    voices: languageVoices,
    voiceName,
    selectVoice,
    rate,
    setRate,
    slowRate,
    setSlowRate,
    speakingId,
    speakText,
    speakWord,
    stop,
  };
};

export default useSpeech;
